<template>
  <v-container
    class="d-flex align-center justify-center"
    style="min-height: 60vh"
  >
    <v-card max-width="600" class="pa-8 text-center">
      <!-- Loading State -->
      <div v-if="isLoading">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          class="mb-4"
        ></v-progress-circular>
        <h2 class="text-h5 mb-2">Processing your request...</h2>
        <p class="text-body-1">Please wait while we update your preferences.</p>
      </div>

      <!-- Success State -->
      <div v-else-if="optOutResult">
        <v-icon
          icon="mdi-check-circle"
          color="success"
          size="64"
          class="mb-4"
        ></v-icon>
        <h2 class="text-h4 mb-4">You have successfully opted out</h2>
        <p class="text-body-1 mb-6">
          Your email preferences have been updated. You will no longer receive
          emails for {{ optOutResult.group.groupName }}.
        </p>
        <div class="d-flex flex-column flex-sm-row gap-3 justify-center">
          <v-btn color="primary" size="large" :to="{ name: 'Manage Opt Outs' }">
            Manage Email Preferences
          </v-btn>
          <v-btn
            color="secondary"
            size="large"
            variant="outlined"
            :to="{ name: 'home' }"
          >
            Return to Homepage
          </v-btn>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError">
        <v-icon
          icon="mdi-alert-circle"
          color="error"
          size="64"
          class="mb-4"
        ></v-icon>
        <h2 class="text-h5 mb-4">Something went wrong</h2>
        <p class="text-body-1 mb-2">{{ errorMessage }}</p>
        <p class="text-body-2 text-grey mb-6">
          Please check your opt-out link or contact support if the problem
          persists.
        </p>
        <div class="d-flex flex-column flex-sm-row gap-3 justify-center">
          <v-btn color="primary" size="large" :to="{ name: 'Manage Opt Outs' }">
            Manage Email Preferences
          </v-btn>
          <v-btn
            color="secondary"
            size="large"
            variant="outlined"
            :to="{ name: 'home' }"
          >
            Return to Homepage
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script lang="ts" setup>
import type { OptOutGroupStatus } from "../../../cbi-generator/generated/dto/OptOutGroupStatus";
import { useOptOutService } from "@/composables/useOptOutService";
import { useRoute } from "vue-router";

const route = useRoute();
const optOutService = useOptOutService();

const isLoading = ref(true);
const optOutResult = ref<OptOutGroupStatus>();
const hasError = ref(false);
const errorMessage = ref("Unable to process your opt-out request.");

const processOptOut = async () => {
  try {
    // Get token from query params
    const token = route.query.token as string;

    if (!token) {
      hasError.value = true;
      errorMessage.value = "Invalid or missing opt-out token.";
      isLoading.value = false;
      return;
    }

    // Call the opt-out API with token and doNotSend flag
    optOutResult.value = await optOutService.optOutUpdatePost({
      token: token,
      doNotSend: true,
    });
  } catch (error: any) {
    console.error("Opt-out error:", error);
    hasError.value = true;

    // Try to extract a more specific error message
    if (error?.response?.data?.message) {
      errorMessage.value = error.response.data.message;
    } else if (error?.message) {
      errorMessage.value = error.message;
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  processOptOut();
});
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
